import { Tag, Avatar, Divider, Row, Col, Image } from "antd";
import {
  Calendar,
  MapPin,
  Users,
  Ticket,
  Image as ImageIcon,
  CheckCircle2,
  Mail,
  Info,
  BookOpen,
  Star,
  Shield,
  Lightbulb,
  Handshake,
  Clock,
  Link2,
} from "lucide-react";
// import { HSN_CODE_OPTIONS } from "pages/Events/commons/events.helpers";
import { SummaryRow, SummarySection } from "Components/SummaryRows/SummaryRows";
import {
  areAllTicketsFree,
  SHOW_IN_LISTINGS_COPY,
} from "pages/Events/commons/events.helpers";


function Step6Review({
  form,
  locations = [],
  canWaivePlatformFee = false,
  platformFeeReset = false,
}) {
  const v = form.getFieldsValue(true);
  const fmt = (d) => d?.format?.("MMM D, YYYY • HH:mm") ?? "";
  const fmtDate = (d) =>
    typeof d === "string" ? d : (d?.format?.("MMM D, YYYY") ?? "");
  const fmtTime = (t) =>
    typeof t === "string" ? t : (t?.format?.("HH:mm") ?? "");
  const isEventPublished = v.publication_status === "PUBLISHED";
  const platformFeeWaived = v.charge_platform_fee === false;
  const isLinkOnly = v.show_in_listings === false;
  const ticketsFree =
    v.publication_status !== "PUBLISHED" && areAllTicketsFree(v.tickets);

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold">Event Summary</h2>
      <p
        style={{
          fontSize: "0.875rem",
          fontWeight: 700,
          color: isEventPublished ? "#22c55e" : "#ef4444",
        }}
      >
        {isEventPublished
          ? "Event Published"
          : `Review everything before submitting`}
      </p>

      {/* ── Event Details ── */}
      <SummarySection icon={Calendar} title="Event Details">
        <SummaryRow label="Title" value={v.title} />
        <SummaryRow label="Description" value={v.description} />
        <SummaryRow label="Event Type" value={v.event_type} />
        <SummaryRow label="Access" value={v.event_access} />
        <SummaryRow
          label={SHOW_IN_LISTINGS_COPY.label}
          value={
            isLinkOnly
              ? SHOW_IN_LISTINGS_COPY.reviewValueOff
              : SHOW_IN_LISTINGS_COPY.reviewValueOn
          }
        />
        {/* <SummaryRow label="HSN Code" value={HSN_CODE_OPTIONS.find((h) => h.value === v.hsn_code)?.label} /> */}
        <SummaryRow label="Featured" value={v.is_featured ? "Yes" : "No"} />
        {v.tags?.length > 0 && (
          <div className="flex gap-3 py-2 border-b text-sm">
            <span className="text-muted-foreground w-44 flex-shrink-0">
              Tags
            </span>
            <div className="flex flex-wrap gap-1">
              {v.tags.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </div>
          </div>
        )}
      </SummarySection>

      {/* ── Dates ── */}
      <SummarySection icon={Clock} title="Date & Time">
        <SummaryRow label="Starts" value={fmt(v.start_date_time)} />
        <SummaryRow label="Ends" value={fmt(v.end_date_time)} />
      </SummarySection>

      {/* ── Venue ── */}
      <SummarySection icon={MapPin} title="Venue">
        {v.location_mode === "existing" ? (
          (() => {
            const loc = locations.find((l) => l.id === v.location_id);
            return loc ? (
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{loc.location_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {[loc.address_line_1, loc.city, loc.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Existing venue selected
              </p>
            );
          })()
        ) : (
          <>
            <SummaryRow label="Name" value={v.location?.name} />
            <SummaryRow
              label="Address Line 1"
              value={v.location?.address_line_1}
            />
            <SummaryRow
              label="Address Line 2"
              value={v.location?.address_line_2}
            />
            <SummaryRow label="City" value={v.location?.city} />
            <SummaryRow label="State" value={v.location?.state} />
            <SummaryRow label="Postal Code" value={v.location?.postal_code} />
            <SummaryRow label="Country" value={v.location?.country_code} />
            <SummaryRow
              label="Coordinates"
              value={
                v.location?.geo_location?.lat
                  ? `${v.location.geo_location.lat}, ${v.location.geo_location.lng}`
                  : null
              }
            />
            <SummaryRow
              label="Venue Email"
              value={v.location?.contact_details?.email}
            />
            <SummaryRow
              label="Venue Phone"
              value={v.location?.contact_details?.phone_number}
            />
            <SummaryRow
              label="Opening Time"
              value={v.location?.operating_hours?.opening_time}
            />
            <SummaryRow
              label="Closing Time"
              value={v.location?.operating_hours?.closing_time}
            />
            {v.location?.operating_hours?.days_of_week?.length > 0 && (
              <div className="flex gap-3 py-2 border-b text-sm">
                <span className="text-muted-foreground w-44 flex-shrink-0">
                  Open Days
                </span>
                <div className="flex flex-wrap gap-1">
                  {v.location.operating_hours.days_of_week.map((d) => (
                    <Tag key={d}>{d}</Tag>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </SummarySection>

      {/* ── Contact ── */}
      <SummarySection icon={Mail} title="Event Contact">
        <SummaryRow label="Email" value={v.contact_email} />
        <SummaryRow label="Phone" value={v.contact_phone} />
      </SummarySection>

      {/* ── Organiser ── */}
      {v.organiser_name && (
        <SummarySection icon={Star} title="Organiser">
          <div className="flex items-center gap-3 mb-2">
            {v.organiser_logo_url && (
              <Avatar src={v.organiser_logo_url} size={48} shape="circle" />
            )}
            <div>
              <p className="text-sm font-semibold">{v.organiser_name}</p>
              {v.organiser_since && (
                <p className="text-xs text-muted-foreground">
                  Hosting since{" "}
                  {typeof v.organiser_since === "string"
                    ? v.organiser_since
                    : v.organiser_since.format("MMM D, YYYY")}
                </p>
              )}
            </div>
          </div>
          <SummaryRow
            label="Total Events Hosted"
            value={v.organiser_total_events}
          />
        </SummarySection>
      )}

      {/* ── Amenities ── */}
      {v.amenities?.filter((a) => a?.name)?.length > 0 && (
        <SummarySection icon={Info} title="Amenities">
          <div className="flex flex-wrap gap-2 pt-1">
            {v.amenities
              .filter((a) => a?.name)
              .map((a, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs bg-muted/30"
                >
                  {a.icon_url && (
                    <Avatar src={a.icon_url} size={16} shape="square" />
                  )}
                  <span>{a.name}</span>
                </div>
              ))}
          </div>
        </SummarySection>
      )}

      {/* ── Activities ── */}
      {v.activities?.filter((a) => a?.name)?.length > 0 && (
        <SummarySection icon={Users} title="Activities / Stages">
          <div className="space-y-2 mt-1">
            {v.activities
              .filter((a) => a?.name)
              .map((a, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 text-sm rounded-lg border bg-muted/20 px-3 py-2"
                >
                  {a.media_url && (
                    <Avatar src={a.media_url} shape="square" size={40} />
                  )}
                  <div>
                    <p className="font-medium">{a.name}</p>
                    {a.description && (
                      <p className="text-xs text-muted-foreground">
                        {a.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </SummarySection>
      )}

      {/* ── Schedule ── */}
      {v.schedule_info?.filter((s) => s?.name)?.length > 0 && (
        <SummarySection icon={Clock} title="Schedule">
          <div className="space-y-1 mt-1">
            {v.schedule_info
              .filter((s) => s?.name)
              .map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-sm py-1.5 border-b last:border-0"
                >
                  <span className="font-medium">{s.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {fmtDate(s.date)}
                    {s.value ? ` • ${fmtTime(s.value)}` : ""}
                  </span>
                </div>
              ))}
          </div>
        </SummarySection>
      )}

      {/* ── Tickets ── */}
      {v.tickets?.length > 0 && (
        <SummarySection icon={Ticket} title="Tickets">
          {(canWaivePlatformFee || platformFeeWaived || platformFeeReset) && (
            <SummaryRow
              label="Platform Fee"
              value={
                ticketsFree
                  ? "No platform fee (tickets are free)"
                  : platformFeeWaived
                  ? "Waived on paid tickets — deducted from your earnings"
                  : platformFeeReset
                    ? "Charged to attendees (your account can no longer waive it)"
                    : "Charged to attendees"
              }
            />
          )}
          <Row gutter={[8, 8]} className="mt-1">
            {v.tickets.map((t, i) => (
              <Col key={i} xs={24} sm={12}>
                <div className="rounded-lg border bg-background px-3 py-2 text-sm flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    {t.description && (
                      <p className="text-xs text-muted-foreground">
                        {t.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">₹{t.price}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.quantity} seats
                    </p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </SummarySection>
      )}

      {/* ── Media ── */}
      {v.media?.length && (
        <SummarySection icon={ImageIcon} title="Event Media">
          <div className="flex flex-wrap gap-3 pt-1">
            {v.media.map((url, i) => (
              <div key={i} className="relative">
                <Image
                  src={url}
                  width={144}
                  alt={i === 0 ? "Primary Banner" : `Image ${i + 1}`}
                  className="object-cover rounded-lg border"
                />
                {i === 0 && (
                  <span className="absolute top-1 left-1 text-[10px] bg-primary text-white rounded px-1.5 py-0.5 font-semibold">
                    Primary
                  </span>
                )}
              </div>
            ))}
          </div>
        </SummarySection>
      )}

      {/* ── FAQ ── */}
      {v.faq?.filter((f) => f?.question)?.length > 0 && (
        <SummarySection icon={BookOpen} title="FAQ">
          <div className="space-y-3 mt-1">
            {v.faq
              .filter((f) => f?.question)
              .map((f, i) => (
                <div key={i} className="text-sm">
                  <p className="font-medium">{f.question}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    {f.answer}
                  </p>
                </div>
              ))}
          </div>
        </SummarySection>
      )}

      {/* ── Things to Know ── */}
      {v.things_to_know?.filter(Boolean)?.length > 0 && (
        <SummarySection icon={Lightbulb} title="Things to Know">
          <ul className="list-disc list-inside space-y-1 mt-1">
            {v.things_to_know.filter(Boolean).map((item, i) => (
              <li key={i} className="text-sm">
                {item}
              </li>
            ))}
          </ul>
        </SummarySection>
      )}

      {/* ── Partners ── */}
      {v.partners?.filter((p) => p?.name)?.length > 0 && (
        <SummarySection icon={Handshake} title="Partners">
          <div className="flex flex-wrap gap-4 pt-1">
            {v.partners
              .filter((p) => p?.name)
              .map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {p.logo_url && (
                    <Avatar src={p.logo_url} size={32} shape="square" />
                  )}
                  <span className="font-medium">{p.name}</span>
                </div>
              ))}
          </div>
        </SummarySection>
      )}

      {/* ── Terms & Conditions ── */}
      {v.tc_title && (
        <SummarySection icon={Shield} title="Terms & Conditions">
          <p className="text-sm font-semibold">{v.tc_title}</p>
          {v.tc_subtitle && (
            <p className="text-xs text-muted-foreground mb-2">
              {v.tc_subtitle}
            </p>
          )}
          {v.tc_sections
            ?.filter((s) => s?.heading)
            ?.map((s, i) => (
              <div key={i} className="text-sm mt-2">
                <p className="font-medium">{s.heading}</p>
                <p className="text-muted-foreground text-xs mt-0.5">
                  {s.content}
                </p>
              </div>
            ))}
        </SummarySection>
      )}

      <Divider />

      {/* Draft notice */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          {/* <p className="font-semibold">
            {isEventPublished ? "Event Published" : "Creating as Draft"}
          </p> */}
          <p className="mt-0.5 text-blue-700">
            {isEventPublished
              ? "Your event is published. You can update event details, content, media, and increase ticket quantities. Date, location, and ticket pricing are locked."
              : `Your event draft is complete and ready to be submitted. Once submitted, it will become eligible for publication and can be published at any time.`}
          </p>
        </div>
      </div>

      {/* Link-only notice */}
      {isLinkOnly && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 flex items-start gap-3">
          <Link2
            className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5"
            aria-hidden
          />
          <p className="mt-0.5 text-blue-700">
            {SHOW_IN_LISTINGS_COPY.reviewNoteOff}
          </p>
        </div>
      )}
    </div>
  );
}

export default Step6Review;
